import React, { createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';

import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import { lazyExecute, yieldToMain } from 'helpers/index';
import { evFacetClick, evFacetSearch } from 'events/search';
import { removeAutofacetFilter } from 'actions/autofacet';
import { getFacetUrlParams, getFacetUrlPath, toggleSelectedFacet } from 'actions/facets';
import { clearAutoComplete } from 'actions/autoComplete';
import { extractFacetGroup, findBreadcrumbByGroup, findMatchingBreadcrumb } from 'helpers/AutofacetUtils';
import type { AppState } from 'types/app';
import type { AutoCompleteDataType, BreadcrumbType, FacetSelectOptions, FacetValueType, NavigationItem } from 'types/search';

export interface FacetsState {
  facets: NavigationItem[];
  autoComplete: AutoCompleteDataType;
  breadcrumbs: BreadcrumbType[];
  isLoading: boolean;
}

export interface FacetsActions {
  handleFacetSelect: (options: FacetSelectOptions) => void;
  handleBreadcrumbPillClick: (breadcrumb: BreadcrumbType) => void;
  trackFacetSearch: (facetGroup: NavigationItem, searchText: string) => void;
}

export interface FacetsMeta {
  hasAutoComplete: boolean;
}

export interface FacetsContextValue {
  state: FacetsState;
  actions: FacetsActions;
  meta: FacetsMeta;
}

const FacetsContext = createContext<FacetsContextValue | null>(null);

const useFacetsContext = () => {
  const context = useContext(FacetsContext);
  if (!context) {
    throw new Error('Facets components must be used within a FacetsProvider');
  }
  return context;
};

interface FacetsProviderProps {
  hasAutoComplete?: boolean;
  onFacetSelect?: (facetGroup: string, facetName: string, selectedFacetGroupIndex: number, selectedFacetIndex: number) => void;
  children: React.ReactNode;
}

/**
 * Reads facets state from Redux and provides
 * unified state/actions/meta to all facets UI components.
 */
const FacetsProvider = ({ hasAutoComplete = false, onFacetSelect, children }: FacetsProviderProps) => {
  const dispatch = useDispatch();
  const facets = useSelector((state: AppState) => state.facets.navigation);
  const autoComplete = useSelector((state: AppState) => state.autoComplete);
  const breadcrumbs = useSelector((state: AppState) => state.filters.breadcrumbs);
  const isLoading = useSelector((state: AppState) => state.products.isLoading);
  const searchTerm = useSelector((state: AppState) => state.filters.originalTerm);

  /**
   * Core workflow for applying any filter change: clear autocomplete, update URL, trigger search.
   * All dispatches are deferred to the next task via yieldToMain() so the browser can paint
   * interaction feedback before React-Redux processes the expensive synchronous re-render.
   */
  const applyFilterChange = async (facetField: string, facetName: string, url: string) => {
    await yieldToMain();

    dispatch(clearAutoComplete(facetField));
    dispatch(getFacetUrlParams(url));
    dispatch(getFacetUrlPath(url));
    dispatch(toggleSelectedFacet(facetField, facetName, null, null, null));
  };

  /**
   * Removes an applied filter and triggers a re-search. State-mutation only —
   * does not fire analytics. Callers that represent a user-initiated breadcrumb
   * removal should use `handleBreadcrumbPillClick` instead, which wraps this
   * with the FACET_PILLS analytics event.
   */
  const removeBreadcrumb = (breadcrumb: BreadcrumbType) => {
    const facetField = extractFacetGroup(breadcrumb.removeName);

    if (breadcrumb.autoFaceted) {
      dispatch(removeAutofacetFilter(breadcrumb));
    }

    applyFilterChange(facetField, breadcrumb.name, breadcrumb.removeUrl);
  };

  /**
   * Public action for the breadcrumb-pill UI: removes the breadcrumb and fires
   * the FACET_PILLS analytics event. Use this from any UI that represents a
   * direct user click on a breadcrumb pill. For sidebar deselects, the
   * LEFT_HAND_FACET_NAV event fires separately from `trackFacetInteraction`.
   */
  const handleBreadcrumbPillClick = (breadcrumb: BreadcrumbType) => {
    removeBreadcrumb(breadcrumb);

    // Defer analytics to avoid blocking the interaction
    lazyExecute(() => {
      track(() => [
        evFacetClick,
        {
          breadcrumbRemove: breadcrumb,
          facetClickSource: 'FACET_PILLS',
          selected: false,
          deselected: true,
          searchTerm
        }
      ]);
      trackEvent('TE_SEARCH_FACET_CLICK', `${breadcrumb.name}:deselected:FACET_PILLS`);
    });
  };

  /**
   * Handles deselecting a facet by finding its breadcrumb for autofacet cleanup.
   * Uses the no-analytics `removeBreadcrumb` helper — the LEFT_HAND_FACET_NAV
   * analytics event for sidebar deselects fires from `trackFacetInteraction`
   * inside `handleFacetSelect` instead. This avoids the double-fire that the
   * old `handleBreadcrumbRemoval` produced for sidebar deselects.
   */
  const handleDeselectFacet = (facetGroup: string, facetValue: FacetValueType) => {
    const matchingBreadcrumb = findMatchingBreadcrumb(facetGroup, facetValue.name, breadcrumbs);

    if (matchingBreadcrumb) {
      removeBreadcrumb(matchingBreadcrumb);
    } else {
      // Fallback: No breadcrumb found (shouldn't happen, but handle gracefully)
      const url = facetValue.facetUrl || facetValue.facetZsoUrl;
      if (url) {
        applyFilterChange(facetGroup, facetValue.name, url);
      }
    }
  };

  /**
   * Handles selecting a new facet.
   * Unlike handleDeselectFacet (which matches by group + value name to find the
   * exact breadcrumb being removed), this matches by group only — we don't care
   * which value is being replaced, just whether *any* value in the group was
   * autofaceted. applyFilterChange handles the actual swap; this only flips the
   * Redux flag so we stop telling the backend to re-apply the autofaceted filter.
   */
  const handleSelectFacet = (facetGroup: string, facetValue: FacetValueType) => {
    const url = facetValue.facetUrl || facetValue.facetZsoUrl;

    if (url) {
      const replacedBreadcrumb = findBreadcrumbByGroup(facetGroup, breadcrumbs);

      if (replacedBreadcrumb?.autoFaceted) {
        dispatch(removeAutofacetFilter(replacedBreadcrumb));
      }

      applyFilterChange(facetGroup, facetValue.name, url);
    }
  };

  /**
   * Tracks analytics for facet interactions.
   */
  const trackFacetInteraction = (facetGroup: string, facetName: string, wasSelected: boolean) => {
    track(() => [
      evFacetClick,
      {
        facetGroup,
        facetName,
        facetClickSource: 'LEFT_HAND_FACET_NAV',
        selected: !wasSelected,
        deselected: wasSelected,
        searchTerm
      }
    ]);
    trackEvent('TE_SEARCH_FACET_CLICK', `${facetGroup}:${facetName}:${wasSelected ? 'deselected' : 'selected'}`);

    if (hasAutoComplete && autoComplete?.[facetGroup]?.text) {
      trackEvent('TE_SEARCH_AUTOCOMPLETE', `${facetGroup}:${autoComplete[facetGroup]?.text || ''}`);
    }
  };

  /**
   * Handles facet selection with analytics tracking and UI updates.
   *
   * Flow:
   * 1. Update filter state (select or deselect)
   * 2. Notify parent component
   * 3. Update UI (close accordion if needed)
   * 4. Track analytics
   */
  const handleFacetSelect = (options: FacetSelectOptions) => {
    const { facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isSelected, facetValue } = options;

    if (!facetValue) return;

    // Step 1: Update filter state
    if (isSelected) {
      handleDeselectFacet(facetGroup, facetValue);
    } else {
      handleSelectFacet(facetGroup, facetValue);
    }
    // Step 2: Notify parent component (legacy callback for special cases + analytics)
    onFacetSelect?.(facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex);

    // Step 3: Track analytics (deferred to avoid blocking the interaction)
    lazyExecute(() => {
      trackFacetInteraction(facetGroup, facetName, isSelected);
    });
  };

  /**
   * Debounced analytics event for facet search.
   */
  const trackFacetSearch = debounce((facetGroup: NavigationItem, searchText: string) => {
    track(() => [
      evFacetSearch,
      {
        autoComplete,
        facetGroup,
        term: searchText
      }
    ]);
  }, 1000);

  return (
    <FacetsContext.Provider
      value={{
        state: { facets: facets ?? [], autoComplete, breadcrumbs, isLoading },
        actions: { handleFacetSelect, handleBreadcrumbPillClick, trackFacetSearch },
        meta: { hasAutoComplete }
      }}
    >
      {children}
    </FacetsContext.Provider>
  );
};

export { FacetsProvider, useFacetsContext, FacetsContext };
